<script setup lang="ts">
import { ref, watch } from 'vue';
import { XMarkIcon, PhotoIcon, ArrowUpTrayIcon } from '@heroicons/vue/24/outline';

interface Props {
  isOpen: boolean;
}

interface Emits {
  (e: 'close'): void;
  (e: 'upload', file: File): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const selectedFile = ref<File | null>(null);
const previewUrl = ref<string | null>(null);
const isDragging = ref(false);
const error = ref<string | null>(null);

watch(() => props.isOpen, (newVal) => {
  if (!newVal) {
    resetState();
  }
});

const resetState = () => {
  selectedFile.value = null;
  previewUrl.value = null;
  error.value = null;
  isDragging.value = false;
};

const validateFile = (file: File): string | null => {
  // Check file type
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  if (!allowedTypes.includes(file.type)) {
    return 'Please upload a valid image file (JPG, PNG, or WEBP)';
  }

  // Check file size (max 5MB)
  const maxSize = 5 * 1024 * 1024;
  if (file.size > maxSize) {
    return 'Image size must be less than 5MB';
  }

  return null;
};

const handleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  
  if (file) {
    processFile(file);
  }
};

const handleDrop = (event: DragEvent) => {
  isDragging.value = false;
  const file = event.dataTransfer?.files[0];
  
  if (file) {
    processFile(file);
  }
};

const processFile = (file: File) => {
  error.value = null;
  
  const validationError = validateFile(file);
  if (validationError) {
    error.value = validationError;
    return;
  }

  selectedFile.value = file;
  
  // Create preview URL
  const reader = new FileReader();
  reader.onload = (e) => {
    previewUrl.value = e.target?.result as string;
  };
  reader.readAsDataURL(file);
};

const handleDragOver = (event: DragEvent) => {
  event.preventDefault();
  isDragging.value = true;
};

const handleDragLeave = () => {
  isDragging.value = false;
};

const handleUpload = () => {
  if (selectedFile.value) {
    emit('upload', selectedFile.value);
  }
};

const handleClose = () => {
  emit('close');
};

const triggerFileInput = () => {
  const input = document.getElementById('file-upload') as HTMLInputElement;
  input?.click();
};
</script>

<template>
  <Transition name="modal">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 z-[9999]"
      @click.self="handleClose"
    >
      <div
        class="bg-white rounded-2xl shadow-[4px_4px_0_0_rgba(0,0,0,1)] border-2 border-black max-w-lg w-full transform transition-all"
        @click.stop
      >
        <!-- Header -->
        <div class="flex items-center justify-between p-6">
          <h2 class="text-xl font-bold text-gray-900">Upload Profile Photo</h2>
          <button
            @click="handleClose"
            class="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon class="w-6 h-6" />
          </button>
        </div>

        <!-- Content -->
        <div class="p-6">
          <!-- Upload Area -->
          <div
            v-if="!previewUrl"
            @drop.prevent="handleDrop"
            @dragover.prevent="handleDragOver"
            @dragleave.prevent="handleDragLeave"
            :class="[
              'border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer',
              isDragging
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50',
            ]"
            @click="triggerFileInput"
          >
            <PhotoIcon class="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <p class="text-lg font-semibold text-gray-700 mb-2">
              Drop your photo here, or <span class="text-blue-600">browse</span>
            </p>
            <p class="text-sm text-gray-500">
              JPG, PNG or WEBP (max. 5MB)
            </p>
            <input
              id="file-upload"
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              class="hidden"
              @change="handleFileSelect"
            />
          </div>

          <!-- Preview Area -->
          <div v-else class="space-y-4">
            <div class="flex flex-col items-center">
              <div class="relative">
                <div class="w-48 h-48 rounded-full overflow-hidden border-4 border-gray-300 shadow-lg">
                  <img
                    :src="previewUrl"
                    alt="Preview"
                    class="w-full h-full object-cover"
                  />
                </div>
                <button
                  @click="resetState"
                  class="absolute top-0 right-0 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg border-2 border-white"
                  title="Remove photo"
                >
                  <XMarkIcon class="w-5 h-5" />
                </button>
              </div>
              <p class="mt-4 text-sm text-gray-500">Preview how your photo will look</p>
            </div>
            
            <div class="bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
              <p class="text-sm text-gray-700">
                <span class="font-semibold">File:</span> {{ selectedFile?.name }}
              </p>
              <p class="text-sm text-gray-700">
                <span class="font-semibold">Size:</span> {{ (selectedFile!.size / 1024 / 1024).toFixed(2) }} MB
              </p>
            </div>
          </div>

          <!-- Error Message -->
          <div
            v-if="error"
            class="mt-4 p-4 bg-red-50 border-2 border-red-200 rounded-lg flex items-start gap-2"
          >
            <XMarkIcon class="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p class="text-sm text-red-700">{{ error }}</p>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-end gap-3 p-6">
          <button
            @click="handleClose"
            class="px-6 py-2.5 text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            @click="handleUpload"
            :disabled="!selectedFile"
            class="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium shadow-md"
          >
            <ArrowUpTrayIcon class="w-5 h-5" />
            Upload Photo
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .bg-white,
.modal-leave-active .bg-white {
  transition: transform 0.3s ease;
}

.modal-enter-from .bg-white,
.modal-leave-to .bg-white {
  transform: scale(0.9);
}
</style>

